import {
  getRouteEditProject,
  getRouteNewVersion,
  getRouteProjectDetails,
  getRouteProjects
} from "@/zapi-frontend/shared/const/router";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { AppButton, FigmaTypography, ConfirmationModal } from "@libs/zapi-ui/custom";
import { Popover, PopoverContent, PopoverTrigger, Tooltip, TooltipContent, TooltipTrigger } from "@libs/zapi-ui/shadcn";
import { HeaderLogo } from "./_HeaderLogo";
import { ProjectIcon } from "@/zapi-frontend/shared/ui/ProjectIconEditor/ProjectIconEditor";
import { ChevronDown, ChevronLeft, Pencil } from "lucide-react";
import { VersionHistoryPanel } from "@/zapi-frontend/pages/ProjectsPage/ui/VersionHistoryPanel/VersionHistoryPanel";
import { ExportButton } from "./_ExportButton";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { ReactNode, useState, useEffect } from "react";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useOrganizationStore } from "@/zapi-frontend/stores/organization";
import { DuplicateProjectModal } from "./DuplicateProjectModal";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { NotificationTrigger } from "@/zapi-frontend/features/Notifications";
import { showAppToast } from "@/zapi-frontend/shared/ui/AppToast";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";
import { useProjectSetupStore } from "@/zapi-frontend/stores/projectSetup";
import { useChapterMemoStore } from "@/zapi-frontend/stores/chapterMemo";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";
import { displayVersionName } from "@/zapi-frontend/shared/i18n/versionDisplayName";
import { useQueryClient } from "@tanstack/react-query";
import { toast as dsToast } from "@libs/zapi-ui/ds/components/toast";
import { GET_PROJECTS_LIST_KEY } from "@/zapi-frontend/shared/services/useGetProjectsList";
import { ProjectVersionMenu } from "@/zapi-frontend/pages/ProjectsPage/ui/ProjectVersionMenu/ProjectVersionMenu";
import { ProjectBreadcrumbHeader } from "./_ProjectBreadcrumbHeader";
import { ConfirmDialog } from "@/zapi-frontend/shared/ui/ConfirmDialog/ConfirmDialog";

export function ProjectDetailsHeader({ end }: { end?: ReactNode }) {
  const navigate = useNavigate();
  const { t } = useTranslation(["projectHome", "common"]);
  const { id: detailsId, projectId: versionSetupId } = useParams();
  const id = detailsId ?? versionSetupId;
  // /projects/new-version/:projectId and /projects/review-relaunch/:projectId configure a version that has no
  // document or exports yet, and nothing on those pages loads the project into the store.
  const isVersionSetup = !detailsId && !!versionSetupId;
  const [searchParams, setSearchParams] = useSearchParams();
  const { role, accessMode } = useMeStore();
  const hasEdit = hasEditAccess(role, accessMode);
  const { selectedProject, duplicateProject, fetchSelectedProject } = useProjectStore();
  const { isSystemAdmin, /* availableOrganizations, */ loadOrganizations, switchOrganization } = useOrganizationStore();
  const { setViewMode } = useChapterStore();
  const { closeHandler } = useProjectSidebarStore();
  const isProjectGenerating = useProjectSetupStore(
    (state) => !!state.pollingProjects[id ?? ""] || !!state.activeSetupTasks[id ?? ""]
  );
  const chapterMemoStatus = useChapterMemoStore((state) => state.chaptersByProject[id ?? ""]);
  const allChapterMemosReady =
    !!chapterMemoStatus && chapterMemoStatus.length > 0 && chapterMemoStatus.every((c) => c.hasActiveMemo);
  const isPresentationDisabled = isProjectGenerating && !allChapterMemosReady;

  const queryClient = useQueryClient();
  const [isVersionPopoverOpen, setIsVersionPopoverOpen] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const currentTab = searchParams.get("tab") || "overview";
  // Home ("overview") is the first page of the Document section, so the Document tab owns
  // every tab value except the presentation one.
  const isDocumentTabActive = currentTab !== "presentation";

  useEffect(() => {
    if (isSystemAdmin) loadOrganizations();
  }, [isSystemAdmin, loadOrganizations]);

  useEffect(() => setIsVersionPopoverOpen(false), [id]);

  useEffect(() => {
    if (!isVersionSetup || !id || selectedProject?.id === id) return;
    void fetchSelectedProject(id);
  }, [isVersionSetup, id, selectedProject?.id, fetchSelectedProject]);

  useEffect(() => {
    if (isVersionSetup) return;
    if (!searchParams.get("tab")) {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set("tab", "overview");
          return params;
        },
        { replace: true }
      );
    }
  }, [isVersionSetup, searchParams, setSearchParams]);

  const handleProjectNameClick = () => {
    setViewMode("projectOverview");
    closeHandler();
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("tab", "overview");
      params.delete("chapterId");
      params.delete("view");
      return params;
    });
  };

  const handleDocumentClick = () => {
    if (isDocumentTabActive) return;

    setViewMode("projectOverview");
    closeHandler();
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("tab", "overview");
      params.delete("chapterId");
      // `view` carries the presentation outline/preview state and means something else on the document side.
      params.delete("view");
      return params;
    });
  };

  const handlePresentationClick = () => setSearchParams({ tab: "presentation" });

  const goToVersion = (versionId: string) => {
    const target = selectedProject?.versions?.find((v) => v.id === versionId);
    navigate(target?.isDraft ? getRouteNewVersion(versionId) : getRouteProjectDetails(versionId));
    if (!target?.name) return;
    if (isVersionSetup) showAppToast({ message: t("header.switchedTo", { name: target.name }) });
    else dsToast.add({ title: t("header.switchedTo", { name: displayVersionName(target.name) }) });
  };

  const handleSelectVersion = (versionId: string) => {
    setIsVersionPopoverOpen(false);
    if (versionId === selectedProject?.id) return;
    goToVersion(versionId);
  };

  const handleCurrentVersionRemoved = (nextVersionId: string | null) => {
    setIsVersionPopoverOpen(false);
    if (!nextVersionId) {
      navigate(getRouteProjects());
      return;
    }
    goToVersion(nextVersionId);
  };

  // const currentOrganization = availableOrganizations.find(
  //   (org: Organization) => org.id === selectedProject?.organizationId
  // );
  // const currentOrgName = currentOrganization?.name;

  function handleEditProject() {
    if (!selectedProject) return;
    navigate(getRouteEditProject(selectedProject.id));
  }

  const handleDuplicateProject = async (organizationId?: number) => {
    if (!selectedProject) return;
    const [, error] = await tryCatch<void, AxiosError<{ message: string }>>(
      duplicateProject(selectedProject.id, organizationId)
    );
    if (error) {
      toast.error(error.response?.data.message || t("header.duplicateFailed"));
      return;
    }

    if (isSystemAdmin && organizationId) switchOrganization(organizationId);

    toast.success(t("header.duplicateSuccess"));
    navigate(getRouteProjects());
  };

  const project = selectedProject?.id === id ? selectedProject : null;

  const DuplicateConfirmation = isVersionSetup ? ConfirmationModal : ConfirmDialog;
  const modals = isSystemAdmin ? (
    <DuplicateProjectModal
      isOpen={isDuplicateModalOpen}
      onClose={() => setIsDuplicateModalOpen(false)}
      onConfirm={handleDuplicateProject}
      currentOrganizationId={selectedProject?.organizationId}
    />
  ) : (
    <DuplicateConfirmation
      isOpen={isDuplicateModalOpen}
      onClose={() => setIsDuplicateModalOpen(false)}
      onConfirm={() => handleDuplicateProject()}
      title={t("projectHome:header.duplicateProject")}
      description={t("projectHome:header.duplicateConfirm")}
      confirmText={t("projectHome:header.duplicateAction")}
      cancelText={t("common:cancel")}
    />
  );

  const versionMenu = project && project.organizationId != null && (
    <ProjectVersionMenu
      projectId={project.id}
      organizationId={project.organizationId}
      currentId={project.id}
      versionGroupId={project.versionGroupId}
      versions={
        project.versions?.length
          ? project.versions
          : [{ id: project.id, name: project.versionDisplayName ?? "", date: project.lastModified ?? "" }]
      }
      hasEdit={hasEdit}
      appearance="plain"
      className="max-w-36 shrink sm:max-w-60"
      onSelect={handleSelectVersion}
      onChanged={() => {
        void fetchSelectedProject(project.id, true);
        void queryClient.invalidateQueries({ queryKey: [GET_PROJECTS_LIST_KEY] });
      }}
      onCurrentRemoved={handleCurrentVersionRemoved}
      onCurrentRestored={(versionId) => navigate(getRouteProjectDetails(versionId))}
    />
  );

  const versionSetupMenu = project?.versionGroupId && project.versionDisplayName && project.organizationId != null && (
    <Popover open={isVersionPopoverOpen} onOpenChange={setIsVersionPopoverOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="header-version-trigger"
          className="flex min-w-0 items-center gap-0.5 rounded-md border border-innoveraZinc-150 py-0.5 pl-2 pr-1.5 hover:bg-innoveraZinc-50"
        >
          <FigmaTypography
            component="span"
            variant="body-2"
            color="zinc900"
            className="max-w-[200px] truncate whitespace-nowrap"
          >
            {displayVersionName(project.versionDisplayName)}
          </FigmaTypography>
          <ChevronDown
            className={cn(
              "size-4 shrink-0 text-innoveraZinc-500 transition-transform",
              isVersionPopoverOpen && "rotate-180"
            )}
            aria-hidden
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={8}
        className="flex w-auto flex-col overflow-hidden border-0 p-0 shadow-none"
        style={{ maxHeight: "var(--radix-popover-content-available-height)" }}
      >
        <VersionHistoryPanel
          rowProjectId={project.id}
          projectName={project.name}
          projectLogoUrl={project.logoUrl}
          projectIconShape={project.iconShape}
          projectIconColor={project.iconColor}
          versions={project.versions ?? []}
          hasEdit={hasEdit}
          organizationId={project.organizationId}
          openInNewTab={false}
          selectedVersionId={project.id}
          onSelectVersion={handleSelectVersion}
          onCurrentVersionRemoved={handleCurrentVersionRemoved}
          onInvalidated={() => void fetchSelectedProject(project.id, true)}
        />
      </PopoverContent>
    </Popover>
  );

  if (!isVersionSetup)
    return (
      <>
        <ProjectBreadcrumbHeader
          project={project}
          versionMenu={versionMenu}
          exportDisabled={isProjectGenerating}
          end={end}
          onProjectNameClick={handleProjectNameClick}
        />
        {modals}
      </>
    );

  return (
    <div className="flex-1 min-w-0 h-full flex items-center">
      {/* Capped so the truncating project name never slides under the viewport-centered tab switcher. */}
      <div className="flex items-center gap-3 min-w-0 max-w-[calc(50vw-140px)]">
        <HeaderLogo />
        {!project && (
          <FigmaTypography variant="body-2" className="animate-pulse">
            {t("common:loadingProject")}
          </FigmaTypography>
        )}
        {project && (
          <>
            <AppButton
              appearance="text"
              size="icon"
              onClick={() => navigate(getRouteProjects())}
              data-testid="header-back-button"
              className="shrink-0"
            >
              <ChevronLeft className="w-4 h-4 text-innoveraZinc-700" />
            </AppButton>
            <div className="flex items-center gap-2 min-w-0">
              <button
                data-testid="project-details-name-button"
                onClick={handleProjectNameClick}
                disabled={isVersionSetup}
                className="flex items-center gap-2 min-w-0"
              >
                <ProjectIcon
                  shape={project.iconShape}
                  color={project.iconColor}
                  logoUrl={project.logoUrl}
                  className="size-8 shrink-0"
                />
                <FigmaTypography
                  variant="label-2"
                  color="zinc900"
                  className="truncate min-w-[48px] max-w-[240px] leading-4"
                >
                  {project.name}
                </FigmaTypography>
              </button>
              {versionSetupMenu}
            </div>
          </>
        )}
      </div>

      {project && (
        <>
          {/* Centered against the whole header, not the space left over by the side blocks (INV-2351). */}
          <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-1">
            <div
              className={cn(
                "flex items-center gap-1 w-fit",
                "border border-innoveraZinc-150 bg-innoveraZinc-100",
                "rounded-[8px]"
              )}
            >
              <AppButton
                data-testid="project-details-document-tab"
                color="white"
                appearance={isDocumentTabActive ? "contained" : "text"}
                onClick={handleDocumentClick}
                disabled={isVersionSetup}
                className={cn(
                  "px-3 rounded-md text-[14px] hover:bg-innoveraZinc-50",
                  isDocumentTabActive
                    ? "text-innoveraZinc-900 font-medium"
                    : "text-innoveraZinc-500 hover:text-innoveraZinc-700"
                )}
              >
                {t("projectHome:header.document")}
              </AppButton>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className={cn(isPresentationDisabled && "cursor-not-allowed")}>
                    <AppButton
                      data-testid="project-details-presentation-tab"
                      color="white"
                      appearance={currentTab === "presentation" ? "contained" : "text"}
                      onClick={handlePresentationClick}
                      disabled={isPresentationDisabled || isVersionSetup}
                      className={cn(
                        "px-3 rounded-md text-[14px] h-[28px] min-h-[28px] hover:bg-innoveraZinc-50",
                        currentTab === "presentation"
                          ? "text-innoveraZinc-900 font-medium"
                          : "text-innoveraZinc-500 hover:text-innoveraZinc-700"
                      )}
                    >
                      {t("projectHome:header.presentation")}
                    </AppButton>
                  </span>
                </TooltipTrigger>
                {isPresentationDisabled && (
                  <TooltipContent side="bottom" sideOffset={8}>
                    <FigmaTypography variant="body-3" color="zinc600">
                      {t("projectHome:header.presentationDisabledTooltip")}
                    </FigmaTypography>
                  </TooltipContent>
                )}
              </Tooltip>
            </div>
          </div>

          <div className="flex items-center gap-1 shrink-0 ml-auto">
            {/* DEPRECATED (INV-2253): edit moved to the Frame tab; the redesign has no header edit button. */}
            {false && hasEdit && (
              <AppButton
                appearance="text"
                size="icon"
                onClick={handleEditProject}
                data-testid="project-header-dropdown-edit"
                title="Edit Project"
                disabled={isProjectGenerating}
              >
                <Pencil
                  className={cn("w-4 h-4", isProjectGenerating ? "text-innoveraZinc-400" : "text-innoveraZinc-700")}
                />
              </AppButton>
            )}
            <ExportButton disabled={isVersionSetup || isProjectGenerating} />
            <div className="w-px h-3 bg-innoveraZinc-200 ml-2" />
            <NotificationTrigger />
            <div className="w-px h-3 bg-innoveraZinc-200 -ml-1" />
          </div>
        </>
      )}

      {modals}
    </div>
  );
}
